import { useNavigate } from 'react-router-dom';

import { ChatBox } from '../../friends/components/ChatBox';

import InfoButton from './InfoButton';

import icPodium from '@/assets/ic_podium.svg';
import { Card } from '@/components';
import { TshirtTeam } from '@/components/TshirtTeam';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useTapTourney from '@/hooks/useTapTourney';
import useTshirtAndShield from '@/hooks/useTshirtAndShield';
import ROUTES from '@/router/routes';
import { SelectedScreen } from '@/types/editProfile';
import { Equipo, ResponseTapTourneyData } from '@/types/tda';
import { cn } from '@/utils';

interface SideContentInfoProps {
  tourneyData: ResponseTapTourneyData | undefined;
}

const StatBlock = ({
  label,
  value,
  className,
  gold,
}: {
  label: string;
  value: string | number | undefined;
  className?: string;
  gold?: boolean;
}) => (
  <div className={`flex gap-3 ${className}`}>
    <p className="text-[14px] text-[#717077]">{label}</p>
    <p
      className={`font-bold ${
        gold ? 'text-[32px] text-[#BA9C06]' : 'text-[24px]'
      }`}
    >
      {value}
    </p>
  </div>
);

const SideContentInfo = ({ tourneyData }: SideContentInfoProps) => {
  const { tapTourneyData, handleSetTapTourney } = useTapTourney();
  const { isDesktop } = useIsDesktop();
  const { userInitialData } = useAuth();
  const navigate = useNavigate();

  const { data: dataTshirtShield } = useTshirtAndShield();

  const handleIsOpenInfo = (boolean: boolean) => {
    handleSetTapTourney({
      ...tapTourneyData,
      isOpenInfo: boolean,
    });
  };

  const getUsersFromTourney = () => {
    const topGeneralUsers =
      tourneyData?.top_general?.map((element: Equipo) => ({
        id: element.idUsuarioDT.toString(),
        display: element.dt,
      })) ?? [];

    const topFechaUsers =
      tourneyData?.top_fecha?.map((element: Equipo) => ({
        id: element.idUsuarioDT.toString(),
        display: element.dt,
      })) ?? [];

    const combinedUsers = [...topGeneralUsers, ...topFechaUsers];
    const uniqueUsers = Array.from(
      new Map(combinedUsers.map((user) => [user.id, user])).values()
    );

    return uniqueUsers;
  };

  const combinedUsers = getUsersFromTourney();

  // Determine which data to show based on active tab
  const rankingPosition = tourneyData?.posicion;
  const points = tourneyData?.puntos_general;

  if (isDesktop) {
    return (
      <div className="flex flex-col gap-y-6 mt-[78px]">
        <Card className={cn('p-0 bg-white w-[426px] ml-8 px-5 pt-6 pb-3')}>
          <div className="flex">
            <figure className="w-[4.5rem]">
              <TshirtTeam
                shield={dataTshirtShield?.shield}
                tshirt={dataTshirtShield?.tshirt}
                withShield={true}
              />
            </figure>
            <div className="ml-7">
              <p className="text-[20px] font-bold text-gdt-gray mb-2">
                {userInitialData?.nombre_equipo}
              </p>
              <span className="text-gdt-gray-3 block text-[16px]">
                {`DT ${userInitialData?.nombre_dt}`}
              </span>
              <button
                className="inline-block bg-transparent underline text-gdt-blue text-[14px] ease duration-200 hover:text-gdt-blue-dark pr-4 mt-2 mb-6 font-bold"
                onClick={() =>
                  navigate(ROUTES.editAccount.myCBU, {
                    state: {
                      returnPage: ROUTES.viewTapTourney,
                    } as SelectedScreen,
                  })
                }
              >
                Modificar datos de CBU
              </button>
            </div>
          </div>
        </Card>

        <Card className={cn('p-0 bg-white w-[426px] ml-8 px-5 pt-6 pb-3')}>
          <div>
            <div className="flex items-center space-x-2 mt-6">
              <figure>
                <img alt={`Icono`} src={icPodium} />
              </figure>
              <p className="font-bold text-[18px]">Mi desempeño</p>
            </div>
            <div className="flex justify-between p-4 mt-4">
              <StatBlock
                gold={true}
                label="RANKING"
                value={`#${rankingPosition ?? '-'}`}
              />
              <StatBlock label="PTS. TORNEO" value={points ?? '-'} />
            </div>
          </div>
        </Card>
        <Card className="p-0 bg-white mt-3 ml-8 w-[426px] rounded-[8px]">
          <ChatBox isTap={true} users={combinedUsers} />
        </Card>
        <div className="flex items-center justify-center ml-4">
          <InfoButton handleIsOpenInfo={handleIsOpenInfo} hideButton={false} />
        </div>
      </div>
    );
  }
};

export default SideContentInfo;
